import type { CSSProperties } from 'react';
import { emptyStates } from '@/constants';
import { getReportedTraceId } from '@/api/errorTrace';

export interface ErrorReferenceProps {
  /** The failed query/request error. */
  error: unknown;
  /** Optional style overrides merged onto the reference line. */
  style?: CSSProperties;
}

/**
 * "Error reference: <traceId>" line shown beneath an error message so a failure
 * is diagnosable from a screenshot alone.
 *
 * A shown reference means an alert exists (#1685, owner Q-550): it renders only
 * when a client-crash report carrying that exact trace id was SENT for this
 * error (`getReportedTraceId`). A failure the router does not report (401, 404,
 * 412, 422, a known refusal, a view-route 4xx, transport, cancel), or whose
 * report was dropped by the session cap or dedup, shows nothing: its id would
 * match no alert. Drop it inside any existing error block:
 * `<ErrorReference error={error} />`.
 */
export function ErrorReference({ error, style }: ErrorReferenceProps) {
  const traceId = getReportedTraceId(error);
  if (!traceId) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7, ...style }}>
      {emptyStates.error.errorReference(traceId)}
    </div>
  );
}
